import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import { IMAGES, ORG } from "@/lib/constants";
import { Mail, ArrowUpRight } from "lucide-react";

const LAST_UPDATED = "May 19, 2026";

function Section({ heading, id, children }) {
  return (
    <section className="mt-10 lg:mt-12" data-testid={`terms-${id}`}>
      <h2 className="font-display text-2xl lg:text-3xl text-white">{heading}</h2>
      <div className="mt-4 space-y-4 text-slate-300 leading-relaxed text-[15px] lg:text-base">
        {children}
      </div>
    </section>
  );
}

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <PageMeta
        title="Terms of Use"
        description="Terms governing your use of the Phoenix of the Detroit Fire Department website, including content use, external links, disclaimers, and contact information."
        path="/terms"
      />

      <PageHero
        overline="Terms"
        title="Terms of Use"
        subtitle="These Terms of Use describe how you may use this website. By using the Phoenix of the Detroit Fire Department website, you agree to these terms. Please read them carefully."
        image={IMAGES.detroit}
      />

      <section className="py-12 lg:py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <p className="text-sm tracking-[0.22em] uppercase font-accent text-amber-500">
            Last updated · {LAST_UPDATED}
          </p>

          <Section heading="About this site" id="about">
            <p>
              This website is published and maintained by the Phoenix of the
              Detroit Fire Department, a Michigan-based 501(c)(3) nonprofit
              organization headquartered at {ORG.address}. The site shares
              information about Phoenix programs, membership, Historic Engine
              11, the 2027 NCR Spring Conference, the Phoenix 60th Anniversary
              Gala, and other Phoenix activities.
            </p>
          </Section>

          <Section heading="Accepting these terms" id="accepting">
            <p>
              By visiting or using this website, you agree to these Terms of
              Use and our{" "}
              <Link
                to="/privacy"
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
                data-testid="terms-privacy-link"
              >
                Privacy Policy
              </Link>
              . If you do not agree with any part of these terms, please do not
              use the site.
            </p>
          </Section>

          <Section heading="Use of website content" id="content">
            <p>
              Content on this website — including text, images, graphics,
              video, the Phoenix logo and badge, the “Phoenix of the Detroit
              Fire Department” name, the 2027 Save-the-Date artwork, the “Black
              Firefighters Matter” identity piece, and other Phoenix branding —
              is the property of Phoenix or used by Phoenix with permission. It
              is provided for personal, non-commercial, informational use only.
            </p>
            <p>
              You may share links to pages on this site freely. You may not
              copy, reproduce, modify, or redistribute Phoenix content for
              commercial purposes without prior written permission from
              Phoenix. Requests for reuse, press inquiries, or media use should
              be sent to{" "}
              <a
                href={`mailto:${ORG.email}`}
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
                data-testid="terms-media-email"
              >
                {ORG.email}
              </a>
              .
            </p>
          </Section>

          <Section heading="Information and accuracy" id="accuracy">
            <p>
              Phoenix works to keep website information current and accurate,
              but content on this site is provided for general informational
              purposes only. Event dates, sponsorship tiers, ticketing,
              programming, conference schedules, and other details may change
              as planning continues. Final details for the 2027 NCR Spring
              Conference and the Phoenix 60th Anniversary Gala will be
              released through Phoenix communications and the sponsorship deck.
            </p>
            <p>
              Nothing on this website is intended as legal, medical, financial,
              or professional advice.
            </p>
          </Section>

          <Section heading="Forms and submissions" id="forms">
            <p>
              When you submit information through Phoenix forms — including the
              Contact form, the newsletter signup, and the Historic Engine 11
              rental inquiry — you agree that the information you provide is
              accurate to the best of your knowledge and that Phoenix may use
              it as described in our{" "}
              <Link
                to="/privacy"
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
            <p>
              Some Phoenix forms are hosted on Zeffy, an external nonprofit
              fundraising and event platform. When you submit a form on Zeffy,
              your submission is processed by Zeffy and subject to Zeffy’s
              own terms of service and privacy policy, available at{" "}
              <a
                href="https://www.zeffy.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline inline-flex items-center gap-1"
                data-testid="terms-zeffy-link"
              >
                www.zeffy.com/privacy-policy <ArrowUpRight className="h-3 w-3" />
              </a>
              .
            </p>
          </Section>

          <Section heading="External links" id="external">
            <p>
              This site may link to third-party websites, including Zeffy,
              Facebook, Instagram, and other partner or sponsor websites.
              Phoenix is not responsible for the content, accuracy, or
              practices of third-party websites. Visiting a third-party site
              from this website is at your own discretion.
            </p>
          </Section>

          <Section heading="Donations and sponsorships" id="donations">
            <p>
              Phoenix is a registered 501(c)(3) nonprofit. Donations and
              sponsorships are processed through Zeffy or through direct
              communication with the Phoenix team. Tax-deductibility of
              contributions is governed by applicable U.S. federal and Michigan
              law. Phoenix provides standard nonprofit donor acknowledgments
              for qualifying contributions.
            </p>
            <p>
              References to sponsorship tiers, table reservations, ad journal
              placements, and per-event sponsorships on this website are
              indicative pending the release of the final Phoenix sponsorship
              deck. Final benefits and pricing will be confirmed in writing
              with each sponsor.
            </p>
          </Section>

          <Section heading="Historic Engine 11 rentals" id="rentals">
            <p>
              Historic Engine 11 rentals are scheduled through the rental
              inquiry form on this website and finalized through a written
              rental agreement with Phoenix. Pricing, availability, capacity,
              and policies described on this website are general guidance and
              will be confirmed in the rental agreement for each event.
            </p>
          </Section>

          <Section heading="Disclaimers and limitation of liability" id="disclaimers">
            <p>
              This website is provided “as is” without warranties of any kind,
              express or implied. Phoenix does not warrant that the website
              will be uninterrupted, error-free, or free from harmful
              components. To the fullest extent permitted by Michigan law,
              Phoenix and its officers, board members, volunteers, and
              affiliates are not liable for any indirect, incidental,
              consequential, or punitive damages arising from your use of the
              website or any content on it.
            </p>
          </Section>

          <Section heading="Changes to these terms" id="changes">
            <p>
              Phoenix may update these Terms of Use from time to time. The
              “Last updated” date at the top of this page will reflect the
              most recent update. Continued use of the website after changes
              are posted constitutes acceptance of the updated terms.
            </p>
          </Section>

          <Section heading="Governing law" id="law">
            <p>
              These Terms of Use are governed by the laws of the State of
              Michigan, without regard to its conflict-of-laws principles. Any
              disputes arising out of or relating to these terms or your use of
              the website will be resolved in the state or federal courts
              located in Wayne County, Michigan.
            </p>
          </Section>

          <Section heading="Contact us about these terms" id="contact">
            <p>
              If you have questions about these Terms of Use or this website,
              please email{" "}
              <a
                href={`mailto:${ORG.email}`}
                className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
              >
                {ORG.email}
              </a>
              , write to us at {ORG.address}, or call {ORG.phone}.
            </p>
            <p className="text-sm text-slate-500 mt-6">
              These Terms of Use are provided as general information about your
              use of this website. They are not legal advice. Phoenix may
              refine these terms following nonprofit legal review.
            </p>
          </Section>

          <div className="mt-12 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="btn-gold w-full sm:w-auto justify-center"
              data-testid="terms-contact-cta"
            >
              <Mail className="h-4 w-4" /> Contact Phoenix
            </Link>
            <Link
              to="/privacy"
              className="btn-ghost w-full sm:w-auto justify-center"
              data-testid="terms-privacy-cta"
            >
              Read Privacy Policy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
